import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type CustomTagDefinition from 'lumen/models/custom-tag-definition';
import type MarketSettingsPropertyTagsCustomRoute from 'lumen/routes/market/settings/property-tags/custom';
import { MAX_CUSTOM_TAG_DEFINITIONS } from 'lumen/utils/constants/property-tags';
import type { ModelFrom } from 'lumen/utils/routes';

export default class MarketSettingsPropertyTagsCustomController extends Controller {
  declare model: ModelFrom<MarketSettingsPropertyTagsCustomRoute>;

  @service declare store: Services['store'];

  @tracked definitionBeingDeleted: CustomTagDefinition | null = null;
  @tracked definitionBeingEdited: CustomTagDefinition | null = null;

  get definitions(): CustomTagDefinition[] {
    return this.model.definitions.last?.value ?? [];
  }

  get isAtDefinitionLimit(): boolean {
    return this.definitions.length >= MAX_CUSTOM_TAG_DEFINITIONS;
  }

  @action
  closeDeleteModal(): void {
    this.definitionBeingDeleted = null;
  }

  @action
  closeEditModal(): void {
    const definition = this.definitionBeingEdited;
    if (definition?.isNew) {
      [...definition.options].forEach((opt) => opt.unloadRecord());
      definition.unloadRecord();
    } else if (definition) {
      definition.options.forEach((opt) => {
        if (opt.isNew) {
          opt.unloadRecord();
        } else {
          opt.rollbackAttributes();
        }
      });
      definition.rollbackAttributes();
    }
    this.definitionBeingEdited = null;
  }

  @action
  isNameUnique(name: string): boolean {
    const trimmed = name.trim().toLowerCase();
    return !this.definitions.some(
      (def) =>
        def.id !== this.definitionBeingEdited?.id &&
        def.name.trim().toLowerCase() === trimmed,
    );
  }

  @action
  onDefinitionDeleted(): void {
    this.definitionBeingDeleted = null;
    this.send('refreshModel');
  }

  @action
  onDefinitionSaved(): void {
    this.definitionBeingEdited = null;
    this.send('refreshModel');
  }

  @action
  openCreateModal(): void {
    this.definitionBeingEdited = this.store.createRecord<CustomTagDefinition>(
      'custom-tag-definition',
      {},
    );
  }

  @action
  openDeleteModal(definition: CustomTagDefinition): void {
    this.definitionBeingDeleted = definition;
  }

  @action
  openEditModal(definition: CustomTagDefinition): void {
    this.definitionBeingEdited = definition;
  }
}
